import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { api, formatApiError } from "@/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Clock, MapPin, Camera, CheckCircle2, X, RotateCcw, Navigation, Lock, CalendarDays } from "lucide-react";

const services = (m) => Array.isArray(m.service_types) ? m.service_types : (m.service_type ? [m.service_type] : []);
const ymd = (d) => {
  const x = new Date(d); const m = String(x.getMonth()+1).padStart(2,"0"); const day = String(x.getDate()).padStart(2,"0");
  return `${x.getFullYear()}-${m}-${day}`;
};

/* ----------------------------- Slot timing ------------------------------ */
// Returns { canStart, reason, openFrom, slotStart, slotEnd }
function slotWindow(mapping, now) {
  const raw = (mapping.slot || "").split("-").map(s => s.trim());
  if (raw.length !== 2) return { canStart: false, reason: "Invalid slot" };
  const todayStr = now.toISOString().slice(0, 10);
  if (mapping.start_date && todayStr < mapping.start_date) return { canStart: false, reason: `Starts ${mapping.start_date}` };
  if (mapping.end_date && todayStr > mapping.end_date) return { canStart: false, reason: "Subscription ended" };

  const [sh, sm] = raw[0].split(":").map(Number);
  const [eh, em] = raw[1].split(":").map(Number);
  if ([sh, sm, eh, em].some(Number.isNaN)) return { canStart: false, reason: "Invalid slot" };
  const slotStart = new Date(now); slotStart.setHours(sh, sm, 0, 0);
  const slotEnd = new Date(now); slotEnd.setHours(eh, em, 0, 0);
  const openFrom = new Date(slotStart.getTime() - 30 * 60 * 1000);

  if (now < openFrom) {
    const mins = Math.ceil((openFrom - now) / 60000);
    const label = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
    return { canStart: false, reason: `Opens in ${label}`, openFrom, slotStart, slotEnd };
  }
  if (now > slotEnd) return { canStart: false, reason: "Slot expired", slotStart, slotEnd };
  return { canStart: true, reason: `Open · until ${raw[1]}`, slotStart, slotEnd };
}

/* ----------------------------- Selfie modal ----------------------------- */
function SelfieDialog({ open, onClose, title, description, ctaLabel, ctaColor = "primary", onSubmit }) {
  const fileRef = useRef();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) { setFile(null); setPreview(null); setBusy(false); } }, [open]);

  const onPick = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFile(f); setPreview(URL.createObjectURL(f));
  };
  const submit = async () => {
    if (!file) { toast.error("Selfie is required"); return; }
    setBusy(true);
    const ok = await onSubmit(file);
    setBusy(false);
    if (ok) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o)=>{ if (!o && !busy) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="selfie-dialog">
        <DialogHeader>
          <DialogTitle className="heading">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="py-3">
          <input ref={fileRef} type="file" accept="image/*" capture="user" className="hidden" onChange={onPick} data-testid="selfie-input" />
          {!preview ? (
            <button type="button" onClick={()=>fileRef.current?.click()}
              className="w-full h-44 rounded-2xl border-2 border-dashed border-border bg-muted flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-primary hover:border-primary transition-colors"
              data-testid="selfie-pick-btn">
              <Camera size={28} strokeWidth={1.5} />
              <span className="text-sm font-medium">Tap to take selfie</span>
              <span className="text-xs">Camera will open · required</span>
            </button>
          ) : (
            <div className="relative rounded-2xl overflow-hidden border border-border">
              <img src={preview} alt="selfie preview" className="w-full max-h-72 object-cover" />
              <button type="button" onClick={()=>{ setFile(null); setPreview(null); }}
                className="absolute top-2 right-2 bg-white/90 hover:bg-white text-foreground rounded-full p-2 shadow"
                title="Retake" data-testid="selfie-retake">
                <RotateCcw size={16}/>
              </button>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={!file || busy}
            className={`rounded-full ${ctaColor === "primary" ? "bg-primary hover:bg-primary/90" : "bg-secondary hover:bg-secondary/90"}`}
            data-testid="selfie-submit">
            {busy ? "Uploading..." : ctaLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* --------------------------- Customer slot card -------------------------- */
function CustomerCard({ mapping, activeSlot, history, canStart, lockedByOther, isGoing, window, onStart, onArrive, onCancelGoing, onEnd }) {
  const startDisabled = !!activeSlot || !canStart || lockedByOther || !window.canStart || isGoing;
  const reason = !canStart ? "Clock in to start a slot"
    : lockedByOther ? "Finish the active slot first"
    : !window.canStart ? window.reason
    : activeSlot ? `${activeSlot.status}` : null;

  return (
    <div className={`card-default transition-opacity ${lockedByOther && !isGoing ? "opacity-60" : ""}`} data-testid={`cust-card-${mapping.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="heading text-xl font-semibold">{mapping.customer_name}</h3>
            {activeSlot && <Badge className="rounded-full bg-secondary text-secondary-foreground capitalize" data-testid="slot-status">{activeSlot.status}</Badge>}
            {isGoing && <Badge className="rounded-full bg-primary text-primary-foreground"><Navigation size={12} className="mr-1"/> En route</Badge>}
          </div>
          <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
            <MapPin size={14} className="shrink-0"/>
            <span className="truncate">{mapping.customer_address || mapping.customer?.profile?.address || "Address pending"}</span>
          </div>
          <div className="flex flex-wrap gap-2 mt-3 text-xs">
            <Badge variant="outline" className="rounded-full">{mapping.slot}</Badge>
            {services(mapping).map(s => <Badge key={s} variant="outline" className="rounded-full capitalize">{s}</Badge>)}
            <Badge variant="outline" className="rounded-full">{mapping.start_date} → {mapping.end_date}</Badge>
            {!activeSlot && (
              <Badge variant={window.canStart ? "default" : "outline"} className={`rounded-full ${window.canStart ? "bg-secondary/15 text-secondary border-secondary/30 hover:bg-secondary/15" : ""}`} data-testid="slot-window">
                <Clock size={12} className="mr-1"/> {window.reason}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* En-route map view */}
      {isGoing && !activeSlot && (
        <div className="mt-4 rounded-2xl overflow-hidden border border-border">
          {(() => {
            const dest = encodeURIComponent(mapping.customer_address || mapping.customer?.profile?.address || "Noida");
            const mapsKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
            const src = mapsKey
              ? `https://www.google.com/maps/embed/v1/place?key=${mapsKey}&q=${dest}&zoom=15`
              : `https://maps.google.com/maps?q=${dest}&z=15&output=embed`;
            return <iframe title={`map-${mapping.id}`} className="w-full h-56" src={src} loading="lazy"/>;
          })()}
          <div className="p-3 flex items-center justify-between bg-muted">
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Navigation size={12}/> Navigate to {mapping.customer_name}
            </div>
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mapping.customer_address || "Noida")}`}
              target="_blank" rel="noreferrer"
              className="text-xs font-medium text-primary hover:underline"
              data-testid={`open-maps-${mapping.id}`}
            >Open in Maps ↗</a>
          </div>
        </div>
      )}

      <div className="mt-5 grid grid-cols-2 gap-3">
        {!isGoing ? (
          <Button
            onClick={onStart}
            disabled={startDisabled}
            className="rounded-full h-12 bg-primary hover:bg-primary/90 disabled:opacity-50"
            data-testid={`start-${mapping.id}`}
          >
            {startDisabled && !activeSlot ? <Lock size={14} className="mr-2"/> : <Camera size={14} className="mr-2"/>}
            Start slot
          </Button>
        ) : (
          <Button
            onClick={onArrive}
            className="rounded-full h-12 bg-primary hover:bg-primary/90"
            data-testid={`arrive-${mapping.id}`}
          >
            <Camera size={14} className="mr-2"/> I've arrived
          </Button>
        )}
        {isGoing ? (
          <Button onClick={onCancelGoing} variant="outline" className="rounded-full h-12 border-2" data-testid={`cancel-going-${mapping.id}`}>
            <X size={14} className="mr-2"/> Cancel
          </Button>
        ) : (
          <Button
            onClick={onEnd}
            disabled={!activeSlot}
            variant="outline"
            className="rounded-full h-12 border-2 disabled:opacity-50"
            data-testid={`end-${mapping.id}`}
          >
            <Camera size={14} className="mr-2"/> End slot
          </Button>
        )}
      </div>

      {reason && !isGoing && <p className="text-xs text-muted-foreground mt-2 capitalize">{reason}</p>}

      {activeSlot && (
        <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
          <div className={`p-2 rounded-lg border ${activeSlot.arrival_selfie_path ? "bg-secondary/10 border-secondary/30 text-secondary-foreground" : "bg-muted border-border text-muted-foreground"}`}>
            {activeSlot.arrival_selfie_path ? <CheckCircle2 size={14} className="inline mr-1 text-secondary"/> : <X size={14} className="inline mr-1"/>}
            Arrival selfie
          </div>
          <div className={`p-2 rounded-lg border ${activeSlot.exit_selfie_path ? "bg-secondary/10 border-secondary/30 text-secondary-foreground" : "bg-muted border-border text-muted-foreground"}`}>
            {activeSlot.exit_selfie_path ? <CheckCircle2 size={14} className="inline mr-1 text-secondary"/> : <X size={14} className="inline mr-1"/>}
            Exit selfie
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Recent</div>
          <div className="space-y-1">
            {history.slice(0, 3).map(s => (
              <div key={s.id} className="flex justify-between text-xs">
                <span className="text-muted-foreground">{new Date(s.started_at).toLocaleString()}</span>
                <Badge variant="outline" className="rounded-full capitalize">{s.status}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------- Dashboard ------------------------------- */
export default function ChefDashboard() {
  const [mappings, setMappings] = useState([]);
  const [slots, setSlots] = useState([]);
  const [weeklyPlans, setWeeklyPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(new Date());
  const [viewDate, setViewDate] = useState(ymd(new Date()));
  const [goingTo, setGoingTo] = useState(null);
  const [startMap, setStartMap] = useState(null);
  const [endSlot, setEndSlot] = useState(null);

  const load = useCallback(async () => {
    try {
      const [mp, sl, wp] = await Promise.all([
        api.get("/chef/mappings"),
        api.get("/chef/slots"),
        api.get("/chef/customer-weekly-plans"),
      ]);
      setMappings(mp.data);
      setSlots(sl.data);
      setWeeklyPlans(wp.data || []);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Tick clock every 30s for time-based gating
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  // Live location ping every 30s — always-on while chef is signed in
  useEffect(() => {
    const send = () => {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(async (pos) => {
        try { await api.post("/chef/location", { lat: pos.coords.latitude, lng: pos.coords.longitude }); } catch {}
      }, () => {}, { enableHighAccuracy: false, timeout: 10000, maximumAge: 30000 });
    };
    send();
    const t = setInterval(send, 30000);
    return () => clearInterval(t);
  }, []);

  const activeFor = (mapId) => slots.find(s => s.mapping_id === mapId && s.status !== "closed");
  const historyFor = (mapId) => slots.filter(s => s.mapping_id === mapId).sort((a,b)=>new Date(b.started_at) - new Date(a.started_at));

  // Exclusivity: chef can be either en-route OR have an active slot for at most one mapping
  const anyActiveSlot = useMemo(() => slots.find(s => s.status !== "closed"), [slots]);
  const lockMapId = goingTo || anyActiveSlot?.mapping_id;

  const handleStart = async (file) => {
    const m = startMap; if (!m) return false;
    try {
      const { data: slot } = await api.post(`/chef/slots/${m.id}/start`);
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/chef/slots/${slot.id}/arrival-selfie`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success(`Slot started for ${m.customer_name}`);
      setGoingTo(null);
      await load();
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return false;
    }
  };

  const handleEnd = async (file) => {
    const s = endSlot; if (!s) return false;
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/chef/slots/${s.id}/exit-selfie`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      await api.post(`/chef/slots/${s.id}/close`);
      toast.success("Slot closed");
      await load();
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return false;
    }
  };

  return (
    <div className="min-h-screen pb-24">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="heading text-3xl font-bold">Today&apos;s kitchen</h1>
            <p className="text-muted-foreground text-sm">Open each customer&apos;s slot, take the arrival selfie, cook, then close with an exit selfie.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="rounded-full"><Clock size={12} className="mr-1"/> {now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</Badge>
          </div>
        </div>

        {/* Customer-wise slots */}
        <div>
          {/* Customer weekly plans (recurring requirements) */}
          {weeklyPlans.length > 0 && (
            <div className="mb-6" data-testid="chef-weekly-plans">
              <h2 className="heading text-xl font-semibold mb-3">Customer weekly plans</h2>
              <div className="grid md:grid-cols-2 gap-3">
                {weeklyPlans.map(wp => (
                  <div key={wp.id} className="card-default" data-testid={`weekly-${wp.customer_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold">{wp.customer_name}</h3>
                      <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
                        Updated {wp.updated_at ? new Date(wp.updated_at).toLocaleDateString() : ""}
                      </span>
                    </div>
                    {(wp.entries || []).length === 0 ? (
                      <p className="text-xs text-muted-foreground">No plan set.</p>
                    ) : (
                      <div className="space-y-1">
                        {wp.entries.map((e, i) => (
                          <div key={i} className="flex items-center justify-between p-2 rounded-md bg-muted/40 text-sm">
                            <div className="capitalize">{e.day}</div>
                            <div className="capitalize text-muted-foreground">{e.service}</div>
                            <div className="text-xs font-medium">{e.pax} pax</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
            <h2 className="heading text-xl font-semibold">Your customers</h2>
            <div className="flex items-end gap-2">
              <div>
                <Label className="text-xs text-muted-foreground flex items-center gap-1"><CalendarDays size={12}/> Viewing date</Label>
                <Input type="date" value={viewDate} onChange={e=>setViewDate(e.target.value)} className="w-44" data-testid="view-date" min={ymd(new Date())} />
              </div>
              <Button variant="ghost" size="sm" className="rounded-full" onClick={()=>setViewDate(ymd(new Date()))} data-testid="view-today">Today</Button>
            </div>
          </div>

          {(() => {
            const todayStr = ymd(new Date());
            const isToday = viewDate === todayStr;
            const visible = mappings.filter(m => {
              const sd = m.start_date || todayStr;
              const ed = m.end_date || sd;
              return viewDate >= sd && viewDate <= ed;
            });
            if (mappings.length === 0) {
              return <div className="card-default text-sm text-muted-foreground">No customers assigned yet. Admin will map you shortly.</div>;
            }
            if (visible.length === 0) {
              return (
                <div className="card-default text-sm text-muted-foreground">
                  No slots scheduled for {viewDate}. {!isToday && <button className="text-primary font-medium" onClick={()=>setViewDate(todayStr)}>Back to today</button>}
                </div>
              );
            }
            return (
              <>
                {!isToday && (
                  <div className="mb-3 px-4 py-2 rounded-full bg-muted text-xs text-muted-foreground inline-flex items-center gap-2">
                    <Lock size={12}/> Preview · slots can only be started on the same day
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-4">
                  {visible.map(m => {
                    const win = isToday ? slotWindow(m, now) : { canStart: false, reason: `Scheduled for ${viewDate}` };
                    const active = isToday ? activeFor(m.id) : null;
                    const isGoing = isToday && goingTo === m.id;
                    const lockedByOther = isToday && !!lockMapId && lockMapId !== m.id;
                    return (
                      <CustomerCard
                        key={m.id}
                        mapping={m}
                        activeSlot={active}
                        history={historyFor(m.id)}
                        canStart={isToday}
                        lockedByOther={lockedByOther}
                        isGoing={isGoing}
                        window={win}
                        onStart={() => isToday && setGoingTo(m.id)}
                        onArrive={() => isToday && setStartMap(m)}
                        onCancelGoing={() => setGoingTo(null)}
                        onEnd={() => isToday && active && setEndSlot(active)}
                      />
                    );
                  })}
                </div>
              </>
            );
          })()}
        </div>

        {/* Leave card removed — leave application has been disabled */}

      </div>

      <SelfieDialog
        open={!!startMap}
        onClose={() => setStartMap(null)}
        title={startMap ? `Arrived at ${startMap.customer_name}` : ""}
        description="Take an arrival selfie to confirm you've reached the customer's location. This is required to start the slot."
        ctaLabel="Confirm arrival & start"
        ctaColor="primary"
        onSubmit={handleStart}
      />      <SelfieDialog
        open={!!endSlot}
        onClose={() => setEndSlot(null)}
        title="End slot"
        description="Take an exit selfie before closing. This is required to end the slot."
        ctaLabel="End slot"
        ctaColor="secondary"
        onSubmit={handleEnd}
      />
    </div>
  );
}
